import { Suspense } from 'react';
import { Await, useLoaderData } from 'react-router-dom';
import type { ContractDetails as ContractDetailsModel } from '@/api/schemas';
import { PreviousPageButton } from '@/components/BackButton';
import { ContractHeader } from '@/components/contract/ContractHeader';
import { DealerSection } from '@/components/contract/DealerSection';
import { PaymentAgreementsSection } from '@/components/contract/PaymentAgreementsSection';
import { PolicySection } from '@/components/contract/PolicySection';
import { TermsSection } from '@/components/contract/TermsSection';
import { useClock } from '@/lib/clock';
import { deriveContractStatus } from '@/lib/contractStatus';
import type { ContractLoaderData, Loaded } from '@/routes/loaders';

function ContractDetailsBody({ contract }: { contract: ContractDetailsModel }) {
  const today = useClock().today();

  return (
    <div className="px-6 pb-6 first:pt-6">
      <ContractHeader
        contract={contract}
        status={deriveContractStatus(contract, today)}
      />

      <div className="mt-2 flex flex-col gap-6 lg:flex-row">
        <div className="flex-1 space-y-4">
          <PolicySection contract={contract} />
          <TermsSection contract={contract} />
        </div>

        <div className="w-full space-y-4 lg:w-[280px]">
          <DealerSection contract={contract} />
          <PaymentAgreementsSection agreement={contract.paymentAgreement} />
        </div>
      </div>
    </div>
  );
}

export function ContractDetails() {
  const data = useLoaderData() as ContractLoaderData;

  return (
    <div className="min-h-full bg-white">
      <PreviousPageButton />

      <Suspense
        fallback={
          <div className="p-8 text-center intl-text-secondary">
            Loading contract...
          </div>
        }
      >
        <Await resolve={data.contract}>
          {(result: Loaded<ContractDetailsModel>) =>
            result.ok ? (
              <ContractDetailsBody contract={result.value} />
            ) : (
              <div className="p-8 text-center intl-text-secondary">
                {result.message}
              </div>
            )
          }
        </Await>
      </Suspense>
    </div>
  );
}
